import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import compose from 'lodash.flowright';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { useQuery } from 'react-apollo';

import Logo from '../../../../../../../components/global/Logo';
import { ALBUM_TYPE } from '../../../../../../../constants/albums';
import { UPLOAD_MODE } from '../../../../../../../constants/album-folder-photos';
import BoxedArrowLink from '../../../../../../../components/global/BoxedArrowLink/BoxedArrowLink';
import { CONTRIBUTOR_SHARE_MODAL } from '../../../../../../../constants/modals';
import { openModal as openModalAction } from '../../../../../../../actions/modal-actions';
import useAlbumFolderUpload from '../../../../../../../components/AlbumFolderUploadMode/hooks';
import useBasename from '../../../../../../../components/routing/useBasename';
import { canShareEntity } from '../../../../../../../util/product-permissions';
import { AlbumWithPurchasedEventAddonTypes } from '../../../../../../../graphql/queries/albums';
import Spinner from '../../../../../../../components/global/Spinner';
import { EVENT_ADDON_ITEM_TYPE } from '../../../../../../../constants/event-items';
import MemoryBookUploadSuccess from './components/MemoryBookUploadSuccess';
import { useContributorPermission } from '../../../../../services/contributors';

const propTypes = {
  albumFolder: PropTypes.shape({
    id: PropTypes.string,
  }),
  uploadMode: PropTypes.oneOf(Object.values(UPLOAD_MODE)),
  onRequestClose: PropTypes.func,
  openModal: PropTypes.func.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
  }),

  albumFolderPhotosTargetAlbums: PropTypes.arrayOf(
    PropTypes.shape({
      albumId: PropTypes.string.isRequired,
      photoGroupId: PropTypes.string,
      displayName: PropTypes.string,
    }),
  ),
  includesGroupPhoto: PropTypes.bool,
};

const AlbumFolderUploadSuccess = ({
  albumFolder,
  uploadMode,
  onRequestClose,
  openModal,
  history,
  albumFolderPhotosTargetAlbums = [],
  includesGroupPhoto,
}) => {
  const basename = useBasename();
  const isShutterbug =
    albumFolder?.album_type === ALBUM_TYPE.SHUTTERBUG ||
    albumFolder?.album_type === ALBUM_TYPE.EVENT;
  const onUpload = useAlbumFolderUpload();
  const isSingleAlbumUpload = albumFolderPhotosTargetAlbums.length === 1;
  const { isContributor } = useContributorPermission({
    entityId: albumFolder?.id,
  });

  useEffect(() => {
    let timeoutId;

    if (
      !includesGroupPhoto &&
      uploadMode === UPLOAD_MODE.PUBLISH &&
      !isShutterbug &&
      onRequestClose
    ) {
      timeoutId = setTimeout(() => {
        onRequestClose();
      }, 3000);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [includesGroupPhoto, uploadMode, isShutterbug, onRequestClose]);

  const { data: { album: albumWithAddonTypes } = {}, loading } = useQuery(
    AlbumWithPurchasedEventAddonTypes,
    {
      variables: {
        id: albumFolderPhotosTargetAlbums?.[0]?.album?.id,
      },
      skip:
        !isSingleAlbumUpload || !albumFolderPhotosTargetAlbums?.[0]?.album?.id,
    },
  );
  const isMemoryBook =
    albumWithAddonTypes?.is_memory_book ||
    albumWithAddonTypes?.purchased_event_addon_types?.find(
      type => type === EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK,
    );
  const onboardingUrl = `/discover/album-folder/${
    albumFolder?.root?.id ?? albumFolder?.id
  }`;

  if (loading) {
    return (
      <div className="text-center py-2">
        <Spinner />
      </div>
    );
  }

  if (isMemoryBook) {
    return (
      <MemoryBookUploadSuccess
        basename={basename}
        albumFolder={albumFolder}
        uploadMode={uploadMode}
        onRequestClose={onRequestClose}
        openModal={openModal}
        history={history}
        target={albumFolderPhotosTargetAlbums?.[0]}
      />
    );
  }

  return (
    <div className="d-flex flex-column">
      <Logo iconOnly className="mr-auto mb-3" imageProps={{ height: '50px' }} />
      <h4 className="text-black text-dark">
        {includesGroupPhoto
          ? 'Your files have been uploaded to review!'
          : 'Your files have been uploaded!'}
      </h4>
      {uploadMode === UPLOAD_MODE.REVIEW && isContributor && (
        <p>your files have been sent to the event owner for review</p>
      )}
      {uploadMode === UPLOAD_MODE.REVIEW &&
        !isContributor &&
        !includesGroupPhoto && (
          <Fragment>
            <p>
              Your unpublished photos can be found within the Review section in
              your album.
            </p>
          </Fragment>
        )}

      {isShutterbug && (
        <Fragment>
          <p className="text-dark text-bold">What to do next?</p>
          <div className="d-flex flex-column flex-md-row">
            <BoxedArrowLink
              className="mr-0 mr-md-2"
              onClick={() => {
                if (isSingleAlbumUpload) {
                  // Single album upload, we can re-focus uploads here:
                  const [target] = albumFolderPhotosTargetAlbums || [];

                  history.push({
                    pathname: `/app/${basename}/albums/${target.album.id}/upload`,
                    state: {
                      album: target.album,
                      photoGroup: target.album_photo_group,
                    },
                  });

                  onRequestClose?.();
                } else {
                  onUpload({ albumFolder, redirectOnUploadModeSetup: true });
                  onRequestClose?.();
                }
              }}
            >
              Upload More Photos
            </BoxedArrowLink>
            {canShareEntity(albumFolder) && (
              <BoxedArrowLink
                onClick={() => {
                  onRequestClose?.();
                  openModal(CONTRIBUTOR_SHARE_MODAL);
                }}
                className="mt-2 mt-md-0"
              >
                Share event
              </BoxedArrowLink>
            )}
            <BoxedArrowLink
              onClick={() => {
                onRequestClose?.();
                history.push(onboardingUrl);
              }}
              className="ml-0 ml-md-2 mt-2 mt-md-0"
            >
              Find my matches!
            </BoxedArrowLink>
          </div>
        </Fragment>
      )}

      {includesGroupPhoto && (
        <p>
          <b>Warning:</b> Do not publish any photos until ALL team photos have
          been uploaded and the <b>TEAM</b> TAG applied to them.
          <br />
          <br />
          Tags can be added to photos by clicking on{' '}
          <span className="text-primary">Select Photos</span> (top right),
          selecting team photos, and clicking{' '}
          <span className="text-primary">Add Tags</span> in the bottom
          navigation.
        </p>
      )}
    </div>
  );
};

AlbumFolderUploadSuccess.propTypes = propTypes;

const mapDispatchToProps = {
  openModal: openModalAction,
};

export default compose(
  withRouter,
  connect(null, mapDispatchToProps),
)(AlbumFolderUploadSuccess);
